import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Headphones, MessageCircle, Clock, Play, Pause, Loader2, Bookmark, BookmarkCheck, Volume2, ListMusic, X, SkipForward, Square } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

export const ParentRegulationTools = () => {
  const [playingAudio, setPlayingAudio] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(null);
  const [bookmarks, setBookmarks] = useState({});
  const [selectedVoice, setSelectedVoice] = useState('echo'); // Echo is smooth and calm - good for meditation
  const [playlist, setPlaylist] = useState([]);
  const [isPlaylistMode, setIsPlaylistMode] = useState(false);
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [showPlaylistPanel, setShowPlaylistPanel] = useState(false);
  const audioRef = useRef(null);
  const { token, API } = useAuth();

  const groundingAudios = [
    {
      id: "body-scan",
      title: "3-Minute Body Scan",
      duration: "3:00",
      description: "Quick body awareness practice to ground yourself",
      script: "Find a comfortable position. Close your eyes if you'd like. Take three slow breaths. Now bring your attention to your feet. Notice any sensations - warmth, coolness, pressure. No need to change anything, just notice. Move your awareness up to your legs... your hips... your belly. Notice your breath moving in and out. Scan up through your chest, shoulders, arms. Notice any tension without judgment. Bring awareness to your neck, jaw, face. Take three more deep breaths. Slowly open your eyes. You're present. You're here."
    },
    {
      id: "emergency-reset",
      title: "2-Minute Emergency Reset",
      duration: "2:00",
      description: "For moments of acute overwhelm",
      script: "Right now, you need a reset. That's okay. Place both feet flat on the floor. Feel them pressing down. This is real. This is now. Take a breath in for 4... hold for 4... out for 4... hold for 4. Again. Notice 5 things you can see. Say them out loud or in your mind. 4 things you can touch - reach out and touch them. 3 things you can hear. 2 things you can smell. 1 thing you can taste. You are here. You are safe. This moment will pass."
    },
    {
      id: "compassion-break",
      title: "3-Minute Compassion Break",
      duration: "3:00",
      description: "Self-compassion when you're running on empty",
      script: "Place a hand on your heart. Feel the warmth. You are doing something incredibly hard. Take a breath. Say to yourself: This is a moment of difficulty. Other parents feel this way too. I am not alone. May I be kind to myself. May I give myself the compassion I need. This is hard, and I don't have to be perfect. I am enough, even on the hard days. Take three more breaths. You can do the next thing. Just the next thing."
    },
    {
      id: "peaceful-pause",
      title: "5-Minute Peaceful Pause",
      duration: "5:00",
      description: "Extended relaxation for deeper calm",
      script: "Allow yourself these five minutes. You deserve this pause. Settle into your seat. Let your shoulders drop away from your ears. Unclench your jaw. Soften your forehead. Take a long, slow breath in through your nose. Hold gently at the top. Release slowly through your mouth. Again. Feel your body becoming heavier, more relaxed with each breath. Imagine a warm, golden light starting at the top of your head. It slowly moves down, relaxing everything it touches. Your face softens. Your neck releases. Your shoulders let go. The warmth moves down your arms to your fingertips. It fills your chest with calm. Your belly rises and falls easily. The warmth continues down through your hips, your legs, all the way to your toes. You are safe. You are held. You are doing your best. When you're ready, take three deep breaths and gently return to the room."
    }
  ];

  const selfTalkScripts = [
    {
      id: "during-flare",
      category: "During Active Flare",
      scripts: [
        "This is the illness, not my child.",
        "This flare will end. They always do.",
        "I can handle the next five minutes. Then the next five.",
        "My child needs me calm, not perfect.",
        "Getting through this moment is enough right now.",
        "I am doing the best I can with what I know today."
      ]
    },
    {
      id: "overwhelmed",
      category: "When Feeling Overwhelmed",
      scripts: [
        "I can ask for help. That doesn't make me weak.",
        "I don't have to have all the answers right now.",
        "It's okay to feel overwhelmed. This is overwhelming.",
        "I can lower my expectations. Survival mode is valid.",
        "I can step away for a moment to breathe.",
        "What would I tell a friend in this situation?"
      ]
    },
    {
      id: "doubting",
      category: "When Doubting Yourself",
      scripts: [
        "I have gotten through 100% of the hard days so far.",
        "Seeking help shows strength, not failure.",
        "I am learning as I go. That's all anyone can do.",
        "Small steps count. They all count.",
        "I see my child. I show up. That matters.",
        "Progress isn't linear, and neither is my journey."
      ]
    },
    {
      id: "late-night",
      category: "Late Night Worry",
      scripts: [
        "2am thoughts are not facts. They're anxiety amplified.",
        "I will think about this tomorrow when I'm rested.",
        "My child is safe right now, in this moment.",
        "Worrying more doesn't help more.",
        "I've handled things before. I will handle them again.",
        "Right now, my only job is to rest."
      ]
    }
  ];

  const urgencyReminders = [
    {
      id: "quick-decisions",
      situation: "When Pressured to Make Quick Decisions",
      reminder: "Most decisions can wait 24 hours. If it can wait, let it wait. You make better decisions when you're not in crisis mode.",
      action: "Say: 'Let me think about that and get back to you tomorrow.'"
    },
    {
      id: "comparing",
      situation: "When Comparing to Other Families",
      reminder: "Every child's PANDAS/PANS journey is unique. What works for others may not apply to you. Focus on your child, your family, your pace.",
      action: "Close social media. Return to what you know about YOUR child."
    },
    {
      id: "research",
      situation: "When Feeling Behind on Research",
      reminder: "You don't need to read everything. You don't need to try everything. You need to do what's sustainable for YOUR family.",
      action: "Choose one reliable source. Read for 15 minutes. Then stop."
    },
    {
      id: "diagnosis",
      situation: "When Diagnosis Feels Urgent",
      reminder: "Getting the right diagnosis is more important than getting a fast diagnosis. It's okay to take time finding the right doctor.",
      action: "Take a breath. You are advocating. That's what matters."
    },
    {
      id: "treatment",
      situation: "When Treatment Isn't Working Yet",
      reminder: "PANDAS/PANS treatment often takes time. Weeks, sometimes months. Lack of immediate improvement doesn't mean it's not working.",
      action: "Track small changes. They matter even when progress feels slow."
    },
    {
      id: "documentation",
      situation: "When You Haven't Documented Everything",
      reminder: "Imperfect documentation is better than perfect documentation that doesn't exist. You don't need a perfect record.",
      action: "Jot down the essentials. That's enough."
    }
  ];

  // Fetch bookmarks on mount
  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks?tool_type=parent-tool`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bookmarkMap = {};
      response.data.forEach(b => {
        bookmarkMap[b.tool_id] = true;
      });
      setBookmarks(bookmarkMap);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const toggleBookmark = async (toolId, toolTitle, category = '') => {
    try {
      if (bookmarks[toolId]) {
        await axios.delete(`${API}/bookmarks/tool/parent-tool/${toolId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookmarks(prev => ({ ...prev, [toolId]: false }));
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/bookmarks`, {
          tool_type: 'parent-tool',
          tool_id: toolId,
          tool_title: toolTitle,
          tool_category: category
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookmarks(prev => ({ ...prev, [toolId]: true }));
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  // Add to playlist
  const addToPlaylist = (audio) => {
    if (playlist.find(p => p.id === audio.id)) {
      toast.info('Already in playlist');
      return;
    }
    setPlaylist(prev => [...prev, audio]);
    toast.success(`Added "${audio.title}" to playlist`);
  };

  // Remove from playlist
  const removeFromPlaylist = (audioId) => {
    setPlaylist(prev => prev.filter(p => p.id !== audioId));
  };

  // Play single audio
  const playAudio = async (audioId, script) => {
    if (playingAudio === audioId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudio(null);
      setIsPlaylistMode(false);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLoadingAudio(audioId);

    try {
      const response = await axios.post(`${API}/tts`, {
        text: script,
        voice: selectedVoice,
        speed: 0.75  // Slower meditation pace
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const audioData = response.data.audio_base64;
      const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingAudio(null);
        audioRef.current = null;
        // If in playlist mode, play next
        if (isPlaylistMode && playlistIndex < playlist.length - 1) {
          playNextInPlaylist();
        } else {
          setIsPlaylistMode(false);
        }
      };

      audio.onerror = () => {
        toast.error('Error playing audio');
        setPlayingAudio(null);
        audioRef.current = null;
        setIsPlaylistMode(false);
      };

      await audio.play();
      setPlayingAudio(audioId);
    } catch (error) {
      console.error('TTS Error:', error);
      toast.error('Failed to generate audio. Please try again.');
    } finally {
      setLoadingAudio(null);
    }
  };

  // Start playing playlist
  const startPlaylist = async () => {
    if (playlist.length === 0) {
      toast.info('Add some audios to your playlist first');
      return;
    }
    setIsPlaylistMode(true);
    setPlaylistIndex(0);
    const firstAudio = playlist[0];
    await playAudio(firstAudio.id, firstAudio.script);
  };

  // Play next in playlist
  const playNextInPlaylist = async () => {
    const nextIndex = playlistIndex + 1;
    if (nextIndex < playlist.length) {
      setPlaylistIndex(nextIndex);
      const nextAudio = playlist[nextIndex];
      await playAudio(nextAudio.id, nextAudio.script);
    } else {
      setIsPlaylistMode(false);
      toast.success('Playlist complete. Take a moment to notice how you feel.');
    }
  };

  // Stop playlist
  const stopPlaylist = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudio(null);
    setIsPlaylistMode(false);
    setPlaylistIndex(0);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const voices = [
    { id: 'echo', name: 'Echo', desc: 'Smooth, calm (recommended)' },
    { id: 'sage', name: 'Sage', desc: 'Wise, measured' },
    { id: 'nova', name: 'Nova', desc: 'Warm, steady' },
    { id: 'shimmer', name: 'Shimmer', desc: 'Gentle, soothing' },
    { id: 'coral', name: 'Coral', desc: 'Warm, friendly' },
  ];

  return (
    <div className="space-y-8" data-testid="parent-regulation-tools">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-4">
          <Headphones className="w-12 h-12 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
            Parent Regulation Tools
          </h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed">
          Quick, practical tools to help you regulate when things feel hard. You can't pour from an empty cup.
        </p>
      </div>

      <Tabs defaultValue="audios" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-white rounded-xl border border-stone-200 p-2">
          <TabsTrigger value="audios" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <Headphones className="w-4 h-4 mr-2" />Grounding Audios
          </TabsTrigger>
          <TabsTrigger value="scripts" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <MessageCircle className="w-4 h-4 mr-2" />Self-Talk Scripts
          </TabsTrigger>
          <TabsTrigger value="reminders" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <Clock className="w-4 h-4 mr-2" />Urgency Reducers
          </TabsTrigger>
        </TabsList>

        {/* Grounding Audios */}
        <TabsContent value="audios" className="space-y-6">
          {/* Controls Card */}
          <Card className="bg-secondary/20 rounded-xl border border-secondary p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-secondary-foreground" />
                <span className="text-[#57534E]">
                  <span className="font-semibold text-secondary-foreground">AI Voice:</span> Meditation-paced audio
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#A8A29E]">Voice:</span>
                  <select 
                    value={selectedVoice}
                    onChange={(e) => setSelectedVoice(e.target.value)}
                    className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 bg-white"
                    data-testid="voice-selector"
                  >
                    {voices.map(v => (
                      <option key={v.id} value={v.id}>{v.name} - {v.desc}</option>
                    ))}
                  </select>
                </div>
                <Button
                  onClick={() => setShowPlaylistPanel(!showPlaylistPanel)}
                  variant={showPlaylistPanel ? "default" : "outline"}
                  className="rounded-full"
                  data-testid="playlist-toggle"
                >
                  <ListMusic className="w-4 h-4 mr-2" />
                  Calm Playlist ({playlist.length})
                </Button>
              </div>
            </div>
          </Card>

          {/* Playlist Panel */}
          {showPlaylistPanel && (
            <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] flex items-center gap-2">
                  <ListMusic className="w-5 h-5 text-indigo-600" />
                  Calm Playlist
                </h3>
                <div className="flex items-center gap-2">
                  {isPlaylistMode ? (
                    <Button
                      onClick={stopPlaylist}
                      className="bg-red-500 text-white hover:bg-red-600 rounded-full px-4"
                      data-testid="stop-playlist"
                    >
                      <Square className="w-4 h-4 mr-2" />
                      Stop
                    </Button>
                  ) : (
                    <Button
                      onClick={startPlaylist}
                      disabled={playlist.length === 0}
                      className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-full px-4"
                      data-testid="start-playlist"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Play All
                    </Button>
                  )}
                </div>
              </div>
              
              {playlist.length === 0 ? (
                <p className="text-[#A8A29E] text-center py-4">
                  Click the + button on audios below to build your playlist
                </p>
              ) : (
                <div className="space-y-2">
                  {playlist.map((audio, index) => (
                    <div 
                      key={audio.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        isPlaylistMode && playlistIndex === index 
                          ? 'bg-indigo-100 border-2 border-indigo-400' 
                          : 'bg-white border border-stone-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <span className="font-medium text-[#2D2D2D]">{audio.title}</span>
                        <span className="text-sm text-[#A8A29E]">{audio.duration}</span>
                      </div>
                      <button
                        onClick={() => removeFromPlaylist(audio.id)}
                        className="p-1 text-[#A8A29E] hover:text-red-500 hover:bg-red-50 rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <p className="text-sm text-indigo-600 text-center pt-2">
                    Total meditation time: ~{playlist.reduce((acc, a) => acc + parseInt(a.duration), 0)} minutes
                  </p>
                </div>
              )}
            </Card>
          )}

          {groundingAudios.map((audio) => (
            <Card key={audio.id} className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-nunito font-bold text-[#2D2D2D]">{audio.title}</h3>
                      <button
                        onClick={() => toggleBookmark(audio.id, audio.title, 'grounding')}
                        className="p-1 hover:bg-stone-100 rounded-full transition-colors"
                        data-testid={`bookmark-${audio.id}`}
                      >
                        {bookmarks[audio.id] ? (
                          <BookmarkCheck className="w-5 h-5 text-primary fill-primary" />
                        ) : (
                          <Bookmark className="w-5 h-5 text-[#A8A29E] hover:text-primary" />
                        )}
                      </button>
                    </div>
                    <p className="text-[#57534E] mb-3">{audio.description}</p>
                    <div className="flex items-center gap-2 text-sm text-[#A8A29E]">
                      <Clock className="w-4 h-4" />
                      <span>{audio.duration}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => addToPlaylist(audio)}
                      variant="outline"
                      className="rounded-full w-10 h-10 p-0"
                      title="Add to Calm Playlist"
                      data-testid={`add-playlist-${audio.id}`}
                    >
                      <ListMusic className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => playAudio(audio.id, audio.script)}
                      disabled={loadingAudio === audio.id}
                      className={`rounded-full w-14 h-14 flex items-center justify-center ${
                        playingAudio === audio.id ? 'bg-accent text-accent-foreground' : 'bg-primary text-white'
                      }`}
                      data-testid={`audio-${audio.id}`}
                    >
                      {loadingAudio === audio.id ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : playingAudio === audio.id ? (
                        <Pause className="w-6 h-6" />
                      ) : (
                        <Play className="w-6 h-6 ml-1" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="bg-stone-50 rounded-lg p-6 border border-stone-200">
                  <p className="text-[#57534E] leading-relaxed whitespace-pre-line italic">
                    {audio.script}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* Self-Talk Scripts */}
        <TabsContent value="scripts" className="space-y-6">
          {selfTalkScripts.map((category) => (
            <Card key={category.id} className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-xl font-nunito font-bold text-[#2D2D2D]">{category.category}</h3>
                <button
                  onClick={() => toggleBookmark(category.id, category.category, 'self-talk')}
                  className="p-1 hover:bg-stone-100 rounded-full transition-colors"
                  data-testid={`bookmark-${category.id}`}
                >
                  {bookmarks[category.id] ? (
                    <BookmarkCheck className="w-5 h-5 text-primary fill-primary" />
                  ) : (
                    <Bookmark className="w-5 h-5 text-[#A8A29E] hover:text-primary" />
                  )}
                </button>
              </div>
              <div className="space-y-3">
                {category.scripts.map((script, j) => (
                  <div key={j} className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors">
                    <span className="text-primary text-2xl leading-none">&quot;</span>
                    <p className="text-[#2D2D2D] leading-relaxed flex-1">{script}</p>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* Urgency Reminders */}
        <TabsContent value="reminders" className="space-y-6">
          {urgencyReminders.map((reminder) => (
            <Card key={reminder.id} className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-secondary-foreground flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]">{reminder.situation}</h3>
                      <button
                        onClick={() => toggleBookmark(reminder.id, reminder.situation, 'urgency')}
                        className="p-1 hover:bg-stone-100 rounded-full transition-colors"
                        data-testid={`bookmark-${reminder.id}`}
                      >
                        {bookmarks[reminder.id] ? (
                          <BookmarkCheck className="w-5 h-5 text-primary fill-primary" />
                        ) : (
                          <Bookmark className="w-5 h-5 text-[#A8A29E] hover:text-primary" />
                        )}
                      </button>
                    </div>
                    <p className="text-[#57534E] leading-relaxed mb-3">{reminder.reminder}</p>
                    <div className="bg-secondary/20 rounded-lg p-4 border-l-4 border-secondary">
                      <p className="text-sm font-semibold text-secondary-foreground mb-1">Try this:</p>
                      <p className="text-[#57534E] italic">{reminder.action}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ParentRegulationTools;
